// Reine Logik der Gespräche (Agent 7D): Filterzustand in der Adresse, Abfrage-Parameter, Suchbegriffe und Hervorhebung, Texte zu
// Aufnahme, Qualität und Live-Dauer, Kurven. Ohne React und ohne Importe aus anderen Dateien außer reinen Hilfen (mit node --test prüfbar).
import type { GespraecheFilter, QualitaetBewertung } from "../api/gespraeche";
import { istZeitraum, zeitraumGrenzen, type Zeitraum } from "../aktivitaeten/zeiten";

export type Uebereinstimmung = "alle" | "irgendein";

export interface VerlaufFilter {
  q: string;
  match: Uebereinstimmung;
  user: string | null;
  lead: string | null;
  leadName: string | null;
  zeitraum: Zeitraum;
  eigenVon: string | null;
  eigenBis: string | null;
  richtung: "eingehend" | "ausgehend" | null;
  /** Mindestdauer in Minuten (Close: „Duration: more than N minutes“) */
  minMinuten: number | null;
  nurAufgenommen: boolean;
  ergebnisse: string[];
  /** auch Anrufe ohne Dauer, Notiz und Aufnahme */
  alle: boolean;
  sort: "neu" | "alt" | "lang";
}

export const LEERER_FILTER: VerlaufFilter = {
  q: "",
  match: "alle",
  user: null,
  lead: null,
  leadName: null,
  zeitraum: "alle",
  eigenVon: null,
  eigenBis: null,
  richtung: null,
  minMinuten: null,
  nurAufgenommen: false,
  ergebnisse: [],
  alle: false,
  sort: "neu",
};

/** Filter aus den Suchparametern der Seite (teilbarer Link). Unbekanntes wird ignoriert. */
export function filterAusParams(p: URLSearchParams): VerlaufFilter {
  const zeit = p.get("zeit");
  const richtung = p.get("richtung");
  const min = Number(p.get("min"));
  const sort = p.get("sort");
  return {
    q: p.get("q") ?? "",
    match: p.get("match") === "irgendein" ? "irgendein" : "alle",
    user: p.get("user") || null,
    lead: p.get("lead") || null,
    leadName: p.get("leadName") || null,
    zeitraum: istZeitraum(zeit) ? zeit : "alle",
    eigenVon: p.get("von") || null,
    eigenBis: p.get("bis") || null,
    richtung: richtung === "eingehend" || richtung === "ausgehend" ? richtung : null,
    minMinuten: Number.isFinite(min) && min > 0 ? Math.min(min, 1440) : null,
    nurAufgenommen: p.get("aufgenommen") === "1",
    ergebnisse: (p.get("ergebnis") ?? "").split(",").filter(Boolean),
    alle: p.get("alle") === "1",
    sort: sort === "alt" || sort === "lang" ? sort : "neu",
  };
}

/** Änderungen am Filter als Suchparameter (leere Werte löschen den Parameter). */
export function paramAenderung(teil: Partial<VerlaufFilter>): Record<string, string | null> {
  const a: Record<string, string | null> = {};
  if ("q" in teil) a.q = teil.q?.trim() ? teil.q.trim() : null;
  if ("match" in teil) a.match = teil.match === "irgendein" ? "irgendein" : null;
  if ("user" in teil) a.user = teil.user || null;
  if ("lead" in teil) a.lead = teil.lead || null;
  if ("leadName" in teil) a.leadName = teil.leadName || null;
  if ("zeitraum" in teil) a.zeit = teil.zeitraum && teil.zeitraum !== "alle" ? teil.zeitraum : null;
  if ("eigenVon" in teil) a.von = teil.eigenVon || null;
  if ("eigenBis" in teil) a.bis = teil.eigenBis || null;
  if ("richtung" in teil) a.richtung = teil.richtung || null;
  if ("minMinuten" in teil) a.min = teil.minMinuten ? String(teil.minMinuten) : null;
  if ("nurAufgenommen" in teil) a.aufgenommen = teil.nurAufgenommen ? "1" : null;
  if ("ergebnisse" in teil) a.ergebnis = teil.ergebnisse?.length ? teil.ergebnisse.join(",") : null;
  if ("alle" in teil) a.alle = teil.alle ? "1" : null;
  if ("sort" in teil) a.sort = teil.sort && teil.sort !== "neu" ? teil.sort : null;
  return a;
}

/** Parameter für `GET /api/gespraeche`. `heute` ist der Berliner Tag (YYYY-MM-DD). */
export function apiFilter(f: VerlaufFilter, heute: string): GespraecheFilter {
  const grenzen = zeitraumGrenzen(f.zeitraum, heute, f.eigenVon, f.eigenBis);
  return {
    user_id: f.user ?? undefined,
    lead_id: f.lead ?? undefined,
    q: f.q.trim() || undefined,
    match: f.q.trim() ? f.match : undefined,
    min_dauer_s: f.minMinuten ? f.minMinuten * 60 : undefined,
    nur_aufgenommen: f.nurAufgenommen || undefined,
    richtung: f.richtung ?? undefined,
    ergebnis: f.ergebnisse.length ? f.ergebnisse.join(",") : undefined,
    von: grenzen.von,
    bis: grenzen.bis,
    alle: f.alle || undefined,
    sort: f.sort === "lang" ? "-dauer" : f.sort === "alt" ? "zeit" : undefined,
  };
}

/** Wie viele Filter sind gesetzt (ohne Suchtext und Sortierung)? Für „Filter leeren“ und die Zahl am Knopf „Weitere Filter“. */
export function weitereFilterZahl(f: VerlaufFilter): number {
  return [f.zeitraum !== "alle", f.richtung !== null, f.minMinuten !== null, f.nurAufgenommen, f.ergebnisse.length > 0, f.alle].filter(Boolean).length;
}

export function filterGesetzt(f: VerlaufFilter): boolean {
  return !!f.q.trim() || f.user !== null || f.lead !== null || weitereFilterZahl(f) > 0;
}

/** Adresse der Detailseite eines Gesprächs (`acti_…` oder `call_crm_…`). */
export function detailPfad(callId: string): string {
  return `/gespraeche/anruf/${encodeURIComponent(callId)}`;
}

// --- Suchbegriffe und Hervorhebung --------------------------------------------------------------------------------------------

/** Wörter der Suche; ein Satz in Anführungszeichen bleibt ein Begriff (wie auf dem Server). */
export function suchbegriffe(q: string | null | undefined): string[] {
  const text = (q ?? "").trim();
  if (!text) return [];
  const begriffe: string[] = [];
  for (const m of text.matchAll(/"([^"]+)"|(\S+)/g)) {
    const b = (m[1] ?? m[2] ?? "").trim();
    if (b) begriffe.push(b);
  }
  return begriffe.slice(0, 8);
}

const ZEICHEN: Record<string, string> = {
  a: "aäàáâÄ",
  e: "eéèêë",
  i: "iíìî",
  o: "oöòóôÖ",
  u: "uüùúûÜ",
  c: "cç",
  n: "nñ",
};

function muster(begriff: string): string {
  const b = begriff.toLowerCase();
  let out = "";
  for (let i = 0; i < b.length; i++) {
    const z = b[i];
    const zwei = b.slice(i, i + 2);
    // Schreibweisen ohne Umlaut finden den Umlaut ("mueller" findet "Müller", "strasse" findet "Straße")
    if (zwei === "ss" || zwei === "ae" || zwei === "oe" || zwei === "ue") {
      out += zwei === "ss" ? "(?:ss|ß)" : `(?:${zwei}|${zwei === "ae" ? "[äÄ]" : zwei === "oe" ? "[öÖ]" : "[üÜ]"})`;
      i += 1;
    } else if (z === "ß") out += "(?:ß|ss)";
    else if (ZEICHEN[z]) out += `[${ZEICHEN[z]}${z.toUpperCase()}]`;
    else out += z.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }
  return out;
}

export interface Textstueck {
  text: string;
  treffer: boolean;
}

/** Text in Stücke teilen; Fundstellen der Suchbegriffe sind `treffer` (Umlaute und Groß-/Kleinschreibung zählen nicht). */
export function hervorheben(text: string, begriffe: readonly string[]): Textstueck[] {
  const brauchbar = begriffe.map((b) => b.trim()).filter((b) => b.length > 0);
  if (!text || !brauchbar.length) return [{ text, treffer: false }];
  let regex: RegExp;
  try {
    regex = new RegExp(`(${brauchbar.map(muster).join("|")})`, "gi");
  } catch {
    return [{ text, treffer: false }];
  }
  const stuecke: Textstueck[] = [];
  let ende = 0;
  for (const m of text.matchAll(regex)) {
    const start = m.index ?? 0;
    if (!m[0]) continue;
    if (start > ende) stuecke.push({ text: text.slice(ende, start), treffer: false });
    stuecke.push({ text: m[0], treffer: true });
    ende = start + m[0].length;
  }
  if (ende < text.length) stuecke.push({ text: text.slice(ende), treffer: false });
  return stuecke.length ? stuecke : [{ text, treffer: false }];
}

/** Notiz kürzen (in Wörtern, nicht mitten im Wort); `gekuerzt` sagt, ob etwas fehlt. */
export function notizAusschnitt(text: string, maxZeichen = 280): { text: string; gekuerzt: boolean } {
  if (text.length <= maxZeichen) return { text, gekuerzt: false };
  const schnitt = text.slice(0, maxZeichen);
  const leer = schnitt.lastIndexOf(" ");
  return { text: (leer > maxZeichen * 0.6 ? schnitt.slice(0, leer) : schnitt).trimEnd(), gekuerzt: true };
}

// --- Aufnahme -----------------------------------------------------------------------------------------------------------------

/** „Läuft ab in 7 Tagen“, „Läuft ab in 5 Std.“, „Abgelaufen“; leer, wenn kein Ablauf bekannt ist. */
export function ablaufText(laeuftAbAm: string | null | undefined, jetzt = Date.now()): string {
  if (!laeuftAbAm) return "";
  const ende = new Date(laeuftAbAm).getTime();
  if (Number.isNaN(ende)) return "";
  const rest = ende - jetzt;
  if (rest <= 0) return "Abgelaufen";
  const min = Math.floor(rest / 60000);
  if (min < 60) return `Läuft ab in ${Math.max(1, min)} Min.`;
  const std = Math.floor(min / 60);
  if (std < 48) return `Läuft ab in ${std} Std.`;
  const tage = Math.floor(std / 24);
  return `Läuft ab in ${tage} Tagen`;
}

/** Stufe für Farbe: rot unter einem Tag, gelb unter drei Tagen. */
export function ablaufTon(laeuftAbAm: string | null | undefined, jetzt = Date.now()): "ok" | "bald" | "kritisch" {
  if (!laeuftAbAm) return "ok";
  const rest = new Date(laeuftAbAm).getTime() - jetzt;
  if (Number.isNaN(rest)) return "ok";
  if (rest < 24 * 3600_000) return "kritisch";
  if (rest < 3 * 24 * 3600_000) return "bald";
  return "ok";
}

// --- Qualität -----------------------------------------------------------------------------------------------------------------

export const BEWERTUNG_TEXT: Record<QualitaetBewertung, string> = { gut: "Gut", mittel: "Mittel", schlecht: "Schlecht" };

/** Zahl mit Dezimalkomma, `stellen` Nachkommastellen; leer bei null. */
export function zahlText(wert: number | null | undefined, stellen = 1): string {
  if (wert === null || wert === undefined || !Number.isFinite(wert)) return "";
  return wert.toLocaleString("de-DE", { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
}

export function prozentText(wert: number | null | undefined): string {
  const t = zahlText(wert, 1);
  return t ? `${t} %` : "";
}

export function msText(wert: number | null | undefined): string {
  const t = zahlText(wert, 0);
  return t ? `${t} ms` : "";
}

/** Ergebnis des Twilio-Gerätetests (Preflight) auf Deutsch. */
export function geraetetestText(wert: string | null | undefined, ok: boolean): string {
  const t: Record<string, string> = { excellent: "ausgezeichnet", great: "sehr gut", good: "gut", fair: "mittel", degraded: "schlecht" };
  return (wert && t[wert]) || (ok ? "in Ordnung" : "auffällig");
}

export const STOERUNG_ART_TEXT: Record<string, string> = {
  fehler: "Fehler beim Aufbau",
  qualitaet: "Qualität",
  mikrofon: "Mikrofon",
  verbindung: "Verbindung",
  haeufung: "Häufung",
};

// --- Live ---------------------------------------------------------------------------------------------------------------------

/** Laufzeit als „2:05“ bzw. „1:02:05“; leer, wenn `seit` fehlt. */
export function laufzeit(seit: string | null | undefined, jetzt = Date.now()): string {
  if (!seit) return "";
  const t = new Date(seit).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.floor((jetzt - t) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${r}` : `${m}:${r}`;
}

// --- Kurven -------------------------------------------------------------------------------------------------------------------

/**
 * Punkte einer Kurve als SVG-Pfad. `werte` mit null sind Lücken. x verteilt die Werte gleichmäßig über `breite`, y läuft von `max`
 * (oben) nach `min` (unten) auf `hoehe`.
 */
export function kurvenPfad(werte: readonly (number | null)[], breite: number, hoehe: number, min: number, max: number): string {
  if (!werte.length || max <= min) return "";
  const schritt = werte.length > 1 ? breite / (werte.length - 1) : 0;
  let pfad = "";
  let offen = false;
  werte.forEach((w, i) => {
    if (w === null || !Number.isFinite(w)) {
      offen = false;
      return;
    }
    const x = werte.length > 1 ? i * schritt : breite / 2;
    const y = hoehe - ((Math.min(max, Math.max(min, w)) - min) / (max - min)) * hoehe;
    pfad += `${offen ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`;
    offen = true;
  });
  return pfad;
}
